import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "../types/pricing";

export default class APIPricingSub extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  // =============================================================================
  // Product Pricing Endpoints
  // =============================================================================

  /**
   * Get all prices (group + partner) for a product (with optional user scope)
   */
  getProductPrices = async (
    productId: string,
    userId?: string,
  ): Promise<types.ProductPricesResponse> => {
    const params: Record<string, string> = {};
    if (userId) params.user_id = userId;
    const res = await this._fetch({
      url: `/products/${productId}/prices`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Get effective price for a product-partner combination
   */
  getEffectivePrice = async (
    productId: string,
    partnerId: string,
    targetDate?: string,
  ): Promise<types.EffectivePriceResponse> => {
    const params: Record<string, string> = { partner_id: partnerId };
    if (targetDate) {
      params.target_date = targetDate;
    }
    const res = await this._fetch({
      url: `/products/${productId}/prices/effective`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Create a client group price for a product
   */
  createGroupPrice = async (
    productId: string,
    data: types.ClientGroupPriceCreateRequest,
    userId?: string,
  ): Promise<types.ClientGroupPriceResponse> => {
    const params: Record<string, string> = {};
    if (userId) params.user_id = userId;
    const res = await this._fetch({
      url: `/products/${productId}/prices/groups`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Update a client group price
   */
  updateGroupPrice = async (
    productId: string,
    priceId: string,
    data: types.ClientGroupPriceUpdateRequest,
  ): Promise<types.ClientGroupPriceResponse> => {
    const res = await this._fetch({
      url: `/products/${productId}/prices/groups/${priceId}`,
      method: "PUT",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Delete a client group price
   */
  deleteGroupPrice = async (
    productId: string,
    priceId: string,
  ): Promise<{ status: string }> => {
    const res = await this._fetch({
      url: `/products/${productId}/prices/groups/${priceId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Create a partner-specific price for a product
   */
  createPartnerPriceForProduct = async (
    productId: string,
    data: types.PartnerPriceForProductCreateRequest,
    userId?: string,
  ): Promise<types.PartnerPriceResponse> => {
    const params: Record<string, string> = {};
    if (userId) params.user_id = userId;
    const res = await this._fetch({
      url: `/products/${productId}/prices/partners`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Update a partner-specific price (from product context)
   */
  updatePartnerPriceForProduct = async (
    productId: string,
    priceId: string,
    data: types.PartnerPriceUpdateRequest,
  ): Promise<types.PartnerPriceResponse> => {
    const res = await this._fetch({
      url: `/products/${productId}/prices/partners/${priceId}`,
      method: "PUT",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Delete a partner-specific price (from product context)
   */
  deletePartnerPriceForProduct = async (
    productId: string,
    priceId: string,
  ): Promise<{ status: string }> => {
    const res = await this._fetch({
      url: `/products/${productId}/prices/partners/${priceId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // =============================================================================
  // Partner Pricing Endpoints
  // =============================================================================

  /**
   * Get all product prices for a partner (with optional user scope)
   */
  getPartnerPrices = async (
    partnerId: string,
    userId?: string,
  ): Promise<types.PartnerPriceResponse[]> => {
    const params: Record<string, string> = {};
    if (userId) params.user_id = userId;
    const res = await this._fetch({
      url: `/partners/${partnerId}/prices`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Get all effective prices for a partner (GROUP and PARTNER prices only, with optional user scope)
   */
  getEffectivePartnerPrices = async (
    partnerId: string,
    userId?: string,
  ): Promise<types.EffectivePartnerPricesResponse> => {
    const params: Record<string, string> = {};
    if (userId) params.user_id = userId;
    const res = await this._fetch({
      url: `/partners/${partnerId}/prices/effective`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Create a product price for a partner
   */
  createPartnerProductPrice = async (
    partnerId: string,
    data: types.PartnerPriceForPartnerCreateRequest,
  ): Promise<types.PartnerPriceResponse> => {
    const res = await this._fetch({
      url: `/partners/${partnerId}/prices`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Update a product price for a partner
   */
  updatePartnerProductPrice = async (
    partnerId: string,
    priceId: string,
    data: types.PartnerPriceUpdateRequest,
  ): Promise<types.PartnerPriceResponse> => {
    const res = await this._fetch({
      url: `/partners/${partnerId}/prices/${priceId}`,
      method: "PUT",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Delete a product price for a partner
   */
  deletePartnerProductPrice = async (
    partnerId: string,
    priceId: string,
  ): Promise<{ status: string }> => {
    const res = await this._fetch({
      url: `/partners/${partnerId}/prices/${priceId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // =============================================================================
  // Client Group Pricing Endpoints
  // =============================================================================

  /**
   * Get all product prices for a client group
   */
  getClientGroupPrices = async (
    groupId: string,
  ): Promise<types.ClientGroupPriceResponse[]> => {
    const res = await this._fetch({
      url: `/client-groups/${groupId}/prices`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Create a product price for a client group
   */
  createClientGroupProductPrice = async (
    groupId: string,
    data: types.ClientGroupPriceForGroupCreateRequest,
  ): Promise<types.ClientGroupPriceResponse> => {
    const res = await this._fetch({
      url: `/client-groups/${groupId}/prices`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Update a product price for a client group
   */
  updateClientGroupProductPrice = async (
    groupId: string,
    priceId: string,
    data: types.ClientGroupPriceUpdateRequest,
  ): Promise<types.ClientGroupPriceResponse> => {
    const res = await this._fetch({
      url: `/client-groups/${groupId}/prices/${priceId}`,
      method: "PUT",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Delete a product price for a client group
   */
  deleteClientGroupProductPrice = async (
    groupId: string,
    priceId: string,
  ): Promise<{ status: string }> => {
    const res = await this._fetch({
      url: `/client-groups/${groupId}/prices/${priceId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // =============================================================================
  // Bulk Pricing Endpoints (for global cache)
  // =============================================================================

  /**
   * Get ALL partner product prices for the company (for global cache)
   */
  getAllPartnerPrices = async (
    userId?: string,
  ): Promise<types.PartnerPriceBulkItem[]> => {
    const params: Record<string, string> = {};
    if (userId) params.user_id = userId;
    const res = await this._fetch({
      url: `/partners/prices/bulk`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Get ALL client group product prices for the company (for global cache)
   */
  getAllGroupPrices = async (
    userId?: string,
  ): Promise<types.ClientGroupPriceBulkItem[]> => {
    const params: Record<string, string> = {};
    if (userId) params.user_id = userId;
    const res = await this._fetch({
      url: `/client-groups/prices/bulk`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Get all product prices for a client group (with optional user scope)
   */
  getClientGroupPricesScoped = async (
    groupId: string,
    userId?: string,
  ): Promise<types.ClientGroupPriceResponse[]> => {
    const params: Record<string, string> = {};
    if (userId) params.user_id = userId;
    const res = await this._fetch({
      url: `/client-groups/${groupId}/prices`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Create a product price for a client group (with optional user scope)
   */
  createClientGroupProductPriceScoped = async (
    groupId: string,
    data: types.ClientGroupPriceForGroupCreateRequest,
    userId?: string,
  ): Promise<types.ClientGroupPriceResponse> => {
    const params: Record<string, string> = {};
    if (userId) params.user_id = userId;
    const res = await this._fetch({
      url: `/client-groups/${groupId}/prices`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Create a partner product price (with optional user scope)
   */
  createPartnerProductPriceScoped = async (
    partnerId: string,
    data: types.PartnerPriceForPartnerCreateRequest,
    userId?: string,
  ): Promise<types.PartnerPriceResponse> => {
    const params: Record<string, string> = {};
    if (userId) params.user_id = userId;
    const res = await this._fetch({
      url: `/partners/${partnerId}/prices`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };
}
